import { z } from 'zod';

import { ZodCurrency } from '@Common/types/Amount';
import { ZodB2bSavingPlanType } from '@Common/types/b2b/plans';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodId, ZodStringIDPathParam } from '@Common/types/request';
import { ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { endpoint, type GetEndpointType } from '@Common/types/zod/zod-endpoint';

import type { BoListQueryBase } from '@Common/types/bo/bo_list_query';

export const RecurringFeeChargeTypeValues = ['b2b_custody_fee', 'b2b_management_fee'] as const;
export const ZodRecurringFeeChargeType = z.enum(RecurringFeeChargeTypeValues);

export const RecurringFeeChargeFrequencyValues = ['yearly', 'monthly', 'quarterly'] as const;
export const ZodRecurringFeeChargeFrequency = z.enum(RecurringFeeChargeFrequencyValues);

export const RecurringFeeChargeStatusValues = [
  'new',
  'executed',
  'insufficient_funds',
  'initiated',
  'skipped',
] as const;
export const ZodRecurringFeeChargeStatus = z.enum(RecurringFeeChargeStatusValues);

export const RecurringFeeChargePaidByValues = ['user', 'company'] as const;
export const ZodRecurringFeeChargePaidBy = z.enum(RecurringFeeChargePaidByValues);

export const ZodGetRecurringFeeChargesOneResponse = z.object({
  id: ZodId,
  user_id: ZodId.nullable(),
  paid_by: ZodRecurringFeeChargePaidBy,
  type: ZodRecurringFeeChargeType,
  b2b_company_plan_type: ZodB2bSavingPlanType,
  status: ZodRecurringFeeChargeStatus,
  amount_current_period: z.number().int(),
  amount_past_uncharged_periods: z.number().int(),
  date: ZodDateTimeString,
  end_date: ZodDateTimeString,
  currency: ZodCurrency,
  charge_frequency: ZodRecurringFeeChargeFrequency,
  created_at: z.date(),
});
export type GetRecurringFeeChargesOneResponse = z.TypeOf<
  typeof ZodGetRecurringFeeChargesOneResponse
>;

export const getRecurringFeeChargesSortColumnValues: readonly (keyof GetRecurringFeeChargesOneResponse)[] =
  ['id', 'type', 'paid_by'] as const;

const ZodGetRecurringFeeChargesFilters = ZodGetRecurringFeeChargesOneResponse.pick({
  id: true,
  user_id: true,
  type: true,
  paid_by: true,
}).partial();

export const ZodGetRecurringFeeChargesQuery = buildZodBoListQueryParsed(
  ZodGetRecurringFeeChargesFilters,
  getRecurringFeeChargesSortColumnValues,
  { range: [0, 100] },
);
export type GetRecurringFeeChargesQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetRecurringFeeChargesFilters>,
  (typeof getRecurringFeeChargesSortColumnValues)[number]
>;

export const ZodGetRecurringFeeChargesResponse = z.array(ZodGetRecurringFeeChargesOneResponse);
export type GetRecurringFeeChargesResponse = z.TypeOf<typeof ZodGetRecurringFeeChargesResponse>;

export const getRecurringFeeChargesEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetRecurringFeeChargesQuery,
  responseBody: ZodGetRecurringFeeChargesResponse,
});

export type GetRecurringFeeChargesEndpoint = GetEndpointType<typeof getRecurringFeeChargesEndpoint>;

export const getRecurringFeeChargeEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodStringIDPathParam,
  responseBody: ZodGetRecurringFeeChargesOneResponse,
});

export type GetRecurringFeeChargeEndpoint = GetEndpointType<typeof getRecurringFeeChargeEndpoint>;
